import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import screenfull from 'screenfull';

import { DeviceInfo, ClientError } from '@ezugi/utils';

import { EVENTS } from '../constants/postMessage';
import { isEvoWrappedSelector, isParentWindowFullscreenSelector } from '../store/selectors';
import { logError } from '../util/logUtils';
import { postMessage } from '../util/postMessage';

export function useFullscreen() {
  const isEvoWrapped = useSelector(isEvoWrappedSelector) as boolean;
  const isOssFullscreen = useSelector(isParentWindowFullscreenSelector) as boolean;

  const [ isFullscreen, setIsFullscreen ] = useState(screenfull.isFullscreen);

  const toggleFullscreen = (on?: boolean) => {
    if (isEvoWrapped) {
      // Fullscreen is controlled by the Evo Wrapper. Fullscreen is disabled within bootstrap
      if (!DeviceInfo.isHandheld) {
        switch (on) {
        case (true): {
          postMessage({ event: EVENTS.ENTER_FULLSCREEN }, false);
          break;
        }
        case (false): {
          postMessage({ event: EVENTS.EXIT_FULLSCREEN }, false);
          break;
        }
        default: {
          const event = isFullscreen ? EVENTS.EXIT_FULLSCREEN : EVENTS.ENTER_FULLSCREEN;
          postMessage({ event }, false);
        }
        }
      }
    } else if (screenfull.isEnabled) {
      // Fullscreen is controlled by bootstrap via screenfull
      try {
        switch (on) {
        case (true): {
          if (!screenfull.isFullscreen) {
            screenfull.request().catch();
          }
          break;
        }
        case (false): {
          if (screenfull.isFullscreen) {
            screenfull.exit();
          }
          break;
        }
        default: {
          screenfull.toggle();
        }
        }
      } catch (err) {
        logError(err as ClientError, 'error');
      }
    }
  };

  useEffect(() => {
    if (isEvoWrapped) {
      // Fullscreen is technically disabled within bootstrap so the "isFullscreen" status is passed down from the Evo Wrapper
      setIsFullscreen(isOssFullscreen);
    } else if (screenfull.isEnabled) {
      // Fullscreen is controlled by bootstrap via screenfull
      const handleChange = () => setIsFullscreen(screenfull.isFullscreen);
      screenfull.on('change', handleChange);
      return () => screenfull.off('change', handleChange);
    }

    return undefined;
  }, [ isEvoWrapped, isOssFullscreen ]);

  return [ isFullscreen, toggleFullscreen ] as const;
}

// this brings back the classic F11 for fullscreen functionality
export const useF11Fullscreen = () => {
  const isEvoWrapped = useSelector(isEvoWrappedSelector) as boolean;

  useEffect(() => {
    if (!DeviceInfo.isHandheld && screenfull.isEnabled && !isEvoWrapped) {
      const handleKey = (event: KeyboardEvent) => {
        if (event.key === 'F11') {
          event.preventDefault();
          screenfull.toggle();
        }
      };

      document.addEventListener('keydown', handleKey, { capture: true });
      return () => document.removeEventListener('keydown', handleKey);
    }
    return undefined;
  }, [ isEvoWrapped ]);
};
